/**
 * src/api/tiktokShopChannel.js
 *
 * TikTok Shop buyer chat (Customer Service API). There is no AeroChat-wide
 * TikTok Shop app yet: each merchant creates their own custom app in TikTok
 * Shop Partner Center and pastes its App Key, App Secret and Service ID here.
 * POST /bot/tiktok-shop/connect stores them and hands back TikTok Shop's
 * authorization URL; after the seller approves, the backend callback lands
 * them back on /channels?channel=tiktok with ?tiktok_shop_connected=1 or
 * ?tiktok_shop_error=<code>.
 */
import { getAuthHeaders } from './auth';
import { getBaseUrl as BASE } from './utils';

async function _parse(r) { const d = await r.json(); if (!r.ok) throw new Error(d?.message || d?.detail || d?.error || 'Request failed'); return d.status === 'success' && d.data ? d.data : (d.data ?? d); }
function _get(p) { return fetch(`${BASE()}${p}`, { headers: { Accept: 'application/json', ...getAuthHeaders() } }).then(_parse); }
function _post(p, b) { return fetch(`${BASE()}${p}`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...getAuthHeaders() }, body: JSON.stringify(b ?? {}) }).then(_parse); }
function _put(p, b) { return fetch(`${BASE()}${p}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...getAuthHeaders() }, body: JSON.stringify(b) }).then(_parse); }
function _del(p) { return fetch(`${BASE()}${p}`, { method: 'DELETE', headers: { Accept: 'application/json', ...getAuthHeaders() } }).then(_parse); }

export async function fetchTikTokShopAccountsFromDb() {
  try { const data = await _get('/bot/tiktok-shop/accounts'); return { exists: Array.isArray(data) && data.length > 0, rows: data || [] }; }
  catch { return { exists: false, rows: [] }; }
}

/** The Redirect URL and Webhook URL the merchant pastes into their custom app. */
export async function getTikTokShopSetupInfo() { return _get('/bot/tiktok-shop/setup-info'); }

/** Save the seller app credentials; resolves to TikTok Shop's authorization URL. */
export async function connectTikTokShop({ appKey, appSecret, serviceId }) {
  const res = await fetch(`${BASE()}/bot/tiktok-shop/connect`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ app_key: appKey, app_secret: appSecret, service_id: serviceId }),
  });
  const d = await res.json();
  if (!res.ok || !d?.auth_url) throw new Error(d?.detail || d?.message || 'Could not save the app details');
  return d.auth_url;
}

export async function getTikTokShopReconnectUrl(accountId) {
  const res = await fetch(`${BASE()}/bot/tiktok-shop/${accountId}/reconnect`, { headers: { Accept: 'application/json', ...getAuthHeaders() } });
  const d = await res.json();
  if (!res.ok || !d?.auth_url) throw new Error(d?.detail || d?.message || 'Could not start reconnect');
  return d.auth_url;
}

export async function deleteTikTokShopRow(accountId) { return _del(`/bot/tiktok-shop/${accountId}`); }

export async function getTikTokShopSettings(accountId) { return _get(`/bot/tiktok-shop/${accountId}/settings`); }

export async function updateTikTokShopSettings(accountId, autoReply) {
  return _put(`/bot/tiktok-shop/${accountId}/settings`, { auto_reply: autoReply });
}

export async function registerTikTokShopWebhooks(accountId) { return _post(`/bot/tiktok-shop/${accountId}/webhooks/register`); }

async function _startSync(path) {
  const res = await fetch(`${BASE()}${path}`, {
    method: 'POST',
    headers: { Accept: 'application/json', ...getAuthHeaders() },
  });
  let d = null;
  try { d = await res.json(); } catch { /* non-JSON error body */ }
  if (!res.ok) {
    const err = new Error(d?.detail || d?.message || 'Request failed');
    err.status = res.status;
    throw err;
  }
  return d?.data?.shops || [];
}

/**
 * Start a product sync for every connected shop.
 * Resolves to [{ id, name, result }], result being 'started', 'missing_permission',
 * 'reconnect_needed' or 'error'. A failed request throws with `status` set
 * (503 = the server does not support product sync yet).
 */
export async function syncTikTokShopProducts() { return _startSync('/bot/tiktok-shop/products/sync'); }

/**
 * Start a promotion and coupon sync for every connected shop. Same shape as
 * syncTikTokShopProducts, plus the result 'started_coupons_only' (the
 * Promotion Information permission is off, so only coupons sync).
 */
export async function syncTikTokShopPromotions() { return _startSync('/bot/tiktok-shop/promotions/sync'); }

/** Buyer's orders, tracking and returns for one TikTok Shop conversation. */
export async function getTikTokShopContactOrders(contactId) { return _get(`/bot/tiktok-shop/contacts/${contactId}/orders`); }

/** Read ?tiktok_shop_connected / ?tiktok_shop_error off the URL once and strip them. */
export function consumeTikTokShopRedirectResult() {
  const q = new URLSearchParams(window.location.search);
  if (!q.has('tiktok_shop_connected') && !q.has('tiktok_shop_error')) return null;
  const result = {
    connected: q.get('tiktok_shop_connected') === '1',
    error: q.get('tiktok_shop_error') || '',
    detail: q.get('detail') || '',
    shop: q.get('shop') || '',
  };
  ['tiktok_shop_connected', 'tiktok_shop_error', 'detail', 'shop', 'channel_id'].forEach(k => q.delete(k));
  const clean = `${window.location.pathname}${q.toString() ? `?${q}` : ''}${window.location.hash}`;
  try { window.history.replaceState({}, '', clean); } catch { /* ignore */ }
  return result;
}
